import { useEffect, useState } from "react";
import adminConfigService, { UploadConfig } from "services/AdminConfig/adminConfigService";
import { buildUploadPolicy, UploadPolicy } from "util/uploadConfig";

// Modules that expose an upload policy via GET api/upload-config/{MODULE}.
export type UploadModule = "ASSET" | "DOCUMENT" | "REIMBURSEMENT";

export interface UseUploadConfigResult extends UploadPolicy {
  /** Raw config last loaded (or the fallback while loading / on failure). */
  config: Partial<UploadConfig>;
  /** True once the fetch has settled (success or failure). */
  loaded: boolean;
}

/**
 * Fetch a module's admin-configured upload policy (allowed file types + max
 * count + max size) and return reusable validators built from it. While the
 * request is in flight — or if it fails — the provided `fallback` keeps the UI
 * usable. Reused by the inventory and reimbursement upload UIs.
 */
export const useUploadConfig = (
  module: UploadModule,
  fallback: Partial<UploadConfig> = {},
): UseUploadConfigResult => {
  const [config, setConfig] = useState<Partial<UploadConfig>>(fallback);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    adminConfigService
      .getUploadConfig(`api/upload-config/${module}`)
      .then((res) => {
        if (active && res.data) setConfig(res.data);
      })
      .catch(() => {
        /* keep the fallback policy */
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [module]);

  return { ...buildUploadPolicy(config), config, loaded };
};

export default useUploadConfig;
